import styled from '@emotion/styled'
import { Card, DESKTOP_BREAKPOINT, straitsXTheme, useModal, useTheme } from '@xfers/design-system'
import React, { Suspense, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Switch, useLocation } from 'react-router-dom'
import { RootState } from '~/app/store/types'
import { selectUserName } from '~/app/store/userInfo/selectors'
import Dpt2Tnc from '~/components/Dpt2Tnc'
import { ErrorBoundary } from '~/components/ErrorBoundary/ErrorBoundary'
import KillSwitchLockedPage from '~/components/KillSwitchLockedPage'
import { PrivateRoute } from '~/components/Route'
import StraitsXFooter from '~/components/StraitsXFooter'
import StraitsXNavigation from '~/components/StraitsXNavigation'
import StraitsXUserInfoBar from '~/components/StraitsXUserInfoBar'
import Tour from '~/components/Tour'
import { useAutoAddPosthogIds } from '~/hooks/useAutoAddPosthogIds'
import { useShield } from '~/hooks/useShield'
import { SITE_TYPE } from '~/managers/ConfigManager'
import { useAppFlippers, useRootFlippers } from '~/store/flippers/hooks'
import { selectUserEmail } from '~/store/route/selectors'
import { useInitRoot } from './RootHooks'
import RouteHooks from './RouteHooks'
import { newStraitsXRoutes as straitsXRoutes } from './routes/config'
import { selectUserInfo, selectUserRequiresManualPeriodicReview } from './store/userInfo/selectors'

export default () => {
  const isSandbox = false
  const location = useLocation()
  const { resetModal } = useModal()
  useAutoAddPosthogIds(SITE_TYPE.APP)

  const { accountLocked, userId } = useSelector(selectUserInfo)
  const { user, isLockedFeatureOn } = useSelector((state: RootState) => state.route)
  const email = useSelector(selectUserEmail)

  useShield(userId)

  const { isEarnEnabled } = useSelector((state: RootState) => state.earn)
  const userName = useSelector(selectUserName)
  const requiresManualPeriodicReview = useSelector(selectUserRequiresManualPeriodicReview)

  useRootFlippers()
  useAppFlippers({ email })

  // fetching application needed data
  const {
    displayGetStartedTab,
    displayMintTab,
    isKillSwitchActive,
    isPeriodicReviewPage,
    notifications,
    shouldRenderDesktopViewNavbar,
    shouldRenderDpt2Tnc,
    shouldRenderTypeformSurvey,
    shouldRenderUserInfoBar,
  } = useInitRoot()

  const memoizedNav = useMemo(() => {
    return (
      <StraitsXNavigation
        displayGetStartedTab={displayGetStartedTab}
        displayMintTab={displayMintTab}
        isSandbox={isSandbox}
        displayEarnTab={isEarnEnabled}
      />
    )
  }, [isSandbox, displayGetStartedTab, displayMintTab, isEarnEnabled])

  if (isKillSwitchActive) {
    return <KillSwitchLockedPage />
  }

  if (shouldRenderDpt2Tnc) {
    return <Dpt2Tnc siteType={SITE_TYPE.APP} />
  }

  return (
    <Root id="app-root">
      {shouldRenderTypeformSurvey && (
        <div
          data-tf-popover="YsqtCzHk"
          data-tf-custom-icon="https://images.typeform.com/images/T62HziiCcpD9"
          data-tf-button-color="#00D37E"
          data-tf-button-text="Launch me"
          data-tf-iframe-props="title=StraitsX Relationship NPS Survey | XSGD"
          data-tf-medium="snippet"
          data-tf-hidden={{ utm_source: 'dashboard', id: email }}
          style={{ all: 'unset' }}
        ></div>
      )}
      <RouteHooks resetModal={resetModal} />
      {isKillSwitchActive !== undefined && (
        <Container>
          {shouldRenderDesktopViewNavbar && <NavigationWrapper>{memoizedNav}</NavigationWrapper>}
          <MainContainer>
            <ErrorBoundary key={location.pathname}>
              {shouldRenderUserInfoBar && (
                <StraitsXUserInfoBar
                  userInfo={{ ...user, fullName: userName }}
                  isSandbox={isSandbox}
                  notifications={notifications}
                  displayGetStartedTab={displayGetStartedTab}
                  displayMintTab={displayMintTab}
                  displayEarnTab={isEarnEnabled}
                />
              )}

              {// only show loading page when not receiving accountLocked from api yet
              accountLocked === null || isLockedFeatureOn === null ? (
                <LoadingPage />
              ) : (
                <Suspense fallback={<LoadingPage />}>
                  <Switch>
                    {straitsXRoutes.map((route, i) => (
                      <PrivateRoute
                        key={i}
                        // eslint-disable-next-line react/jsx-props-no-spreading
                        {...route}
                        isAccountLocked={accountLocked}
                        isLockedFeatureOn={isLockedFeatureOn}
                        isPeriodicReviewRequired={requiresManualPeriodicReview}
                      />
                    ))}
                  </Switch>
                </Suspense>
              )}
              {!isPeriodicReviewPage && <StraitsXFooter />}
            </ErrorBoundary>
          </MainContainer>
          <Tour />
        </Container>
      )}
    </Root>
  )
}

function LoadingPage() {
  const { spacing } = useTheme()

  return (
    <Page>
      <Card style={{ padding: spacing.xl }}>
        <Skeleton style={{ width: '40%' }} />
        <Skeleton />
        <Skeleton />
      </Card>
    </Page>
  )
}

const Page = styled.div`
  padding: 16px;

  ${DESKTOP_BREAKPOINT} {
    padding: 24px;
  }
`

const Root = styled.div`
  z-index: 0;
`

const Container = styled.div`
  background-size: 100%;
  background-color: ${straitsXTheme.color.background.secondaryNeutral};
  -webkit-font-smoothing: antialiased;
  display: flex;
`

const MainContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 73px;
  width: 100%;

  ${DESKTOP_BREAKPOINT} {
    padding-top: 0;
  }
`

const NavigationWrapper = styled.div`
  z-index: 1;
  display: none;

  ${DESKTOP_BREAKPOINT} {
    display: flex;
    width: 257px;
    flex-shrink: 0;
  }
`

const Skeleton = styled.div`
  float: left;
  width: 100%;
  height: 16px;

  margin: 10px;
  border-radius: 5px;
  background-size: 100wh;
  background-image: linear-gradient(
    90deg,
    ${straitsXTheme.color.base.line} 0%,
    ${straitsXTheme.color.background.secondaryNeutral} 50%,
    ${straitsXTheme.color.base.line} 100%
  );
  animation: shine-lines 1s infinite linear;

  @keyframes shine-lines {
    0% {
      background-position: -100px;
    }
    40%,
    100% {
      background-position: 140px;
    }
  }
`
